import React, { useEffect, useState, useRef } from 'react';
import {
    Modal,
    IconButton,
    Box,
    useMediaQuery
} from '@mui/material';
import {
    Close,
    ArrowBack,
    ArrowForward,
    PlayArrow,
    Pause
} from '@mui/icons-material';
import {
    TransformWrapper,
    TransformComponent
} from 'react-zoom-pan-pinch';
import { useSwipeable } from 'react-swipeable';
import { useTheme } from '@mui/material/styles';

const ImageViewer = ({ images = [], open, onClose, initialIndex = 0 }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [showImages, setShowImages] = useState([]);
    const [isPlaying, setIsPlaying] = useState(false);
    const [direction, setDirection] = useState(null);

    const transformRef = useRef(null);
    const slideshowRef = useRef(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const updatedImages = images.map((img) => img.surl || img.turl);
        setShowImages(updatedImages);
    }, [images]);

    useEffect(() => {
        if (open) {
            setCurrentIndex(initialIndex);
            setIsPlaying(false);
        }
    }, [open, initialIndex]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!open || showImages.length <= 1) return;

            if (e.key === 'ArrowRight' || e.key === 'Enter') {
                handleNext();
            } else if (e.key === 'ArrowLeft') {
                handlePrev();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, showImages.length]);


    useEffect(() => {
        const style = document.createElement("style");
        style.innerHTML = `
      @keyframes slideInRight {
        0% { transform: translateX(100%); opacity: 0; }
        100% { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideInLeft {
        0% { transform: translateX(-100%); opacity: 0; }
        100% { transform: translateX(0); opacity: 1; }
      }
    `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);


    useEffect(() => {
        if (isPlaying) {
            slideshowRef.current = setInterval(() => {
                handleNext(true);
            }, 3000);
        } else {
            clearInterval(slideshowRef.current);
        }
        return () => clearInterval(slideshowRef.current);
    }, [isPlaying, showImages.length]);

    const handleNext = (isAuto = false) => {
        transformRef.current?.resetTransform();
        setDirection('right');
        setCurrentIndex((prev) => (prev + 1) % showImages.length);
        if (!isAuto) setIsPlaying(false);
    };

    const handlePrev = () => {
        transformRef.current?.resetTransform();
        setDirection('left');
        setCurrentIndex((prev) => (prev - 1 + showImages.length) % showImages.length);
        setIsPlaying(false);
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: showImages.length > 1 ? () => handleNext(false) : undefined,
        onSwipedRight: showImages.length > 1 ? handlePrev : undefined,
        preventScrollOnSwipe: true,
        trackTouch: true,
        trackMouse: false,
    });

    const toggleSlideshow = () => setIsPlaying((prev) => !prev);

    return (
        <Modal open={open} onClose={() => {
            onClose();
            setIsPlaying(false);
        }}>
            <Box
                {...swipeHandlers}
                height="100vh"
                width="100vw"
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                    backdropFilter: 'blur(12px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    zIndex: 1500,
                }}
            >
                <IconButton
                    onClick={() => {
                        onClose();
                        setIsPlaying(false);
                    }}
                    sx={{
                        color: 'white',
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        zIndex: 2000,
                    }}
                >
                    <Close fontSize="large" />
                </IconButton>

                {/* Left Arrow */}
                {showImages.length > 1 && (
                    <IconButton
                        onClick={handlePrev}
                        sx={{
                            color: 'white',
                            position: 'absolute',
                            left: 16,
                            zIndex: 1000,
                        }}
                    >
                        <ArrowBack fontSize="large" />
                    </IconButton>
                )}

                {showImages.length > 1 && (
                    <IconButton
                        onClick={toggleSlideshow}
                        sx={{
                            color: 'white',
                            position: 'absolute',
                            bottom: 24,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#00000088',
                            '&:hover': { backgroundColor: '#000000cc' },
                            zIndex: 1200,
                        }}
                    >
                        {isPlaying ? <Pause fontSize="large" /> : <PlayArrow fontSize="large" />}
                    </IconButton>
                )}

                <TransformWrapper
                    ref={transformRef}
                    key={currentIndex}
                    initialScale={1}
                    minScale={1}
                    maxScale={3}
                    wheel={{ step: 0.1 }}
                    pinch={{ step: 5 }}
                    doubleClick={{ disabled: true }}
                >
                    {({ zoomIn, resetTransform, state }) => (
                        <TransformComponent
                            wrapperStyle={{ touchAction: 'manipulation' }}
                            contentStyle={{ touchAction: 'manipulation' }}
                            onDoubleClick={() => {
                                if (state.scale === 1) {
                                    zoomIn(2);
                                } else {
                                    resetTransform();
                                }
                            }}
                        >
                            <img
                                src={showImages[currentIndex]}
                                alt={`Image ${currentIndex + 1}`}
                                className={`slide-${direction}`}
                                style={{
                                    maxHeight: '90vh',
                                    maxWidth: '90vw',
                                    objectFit: 'contain',
                                    borderRadius: '8px',
                                    userSelect: 'none',
                                    animation: direction
                                        ? `${direction === 'right' ? 'slideInRight' : 'slideInLeft'} 0.4s ease`
                                        : 'none',
                                }}
                                onAnimationEnd={() => setDirection(null)}
                            />
                        </TransformComponent>
                    )}
                </TransformWrapper>

                {showImages.length > 1 && (
                    <IconButton
                        onClick={() => handleNext(false)}
                        sx={{
                            color: 'white',
                            position: 'absolute',
                            right: 16,
                            zIndex: 1000,
                        }}
                    >
                        <ArrowForward fontSize="large" />
                    </IconButton>
                )}
            </Box>
        </Modal>
    );
};

export default ImageViewer;